import { queryOptions } from "@tanstack/react-query";
import { createQueryGroup } from "../../core/api/queries";
import { components, PatientId } from "../../shared/schema/schema";
import { IcdCodeAutoCompleteResult } from "./types/medical-diagnosis-code.types";

export default createQueryGroup(({ api }) => {
  return {
    get: (id: PatientId) =>
      queryOptions({
        queryKey: ["patient", "get", id],
        queryFn: () =>
          api.get("/agencies/:agencyId/patients/:patientId", { path: { patientId: id } }),
      }),
    search: (term: string) =>
      queryOptions({
        queryKey: ["patient", "search", term],
        queryFn: () => {
          return term.length > 0
            ? api.get("./patients/search", { query: { term } })
            : { patients: [] };
        },
      }),
    communicationLog: (id: PatientId) =>
      queryOptions({
        queryKey: ["patient", id, "communicationLog"],
        queryFn: () =>
          api.get("./comm_center/tickets/patients/:patientId", {
            path: { patientId: id },
          }),
      }),
    taskDocumentsChangeRequests: (patientId: PatientId) =>
      queryOptions({
        queryKey: ["patient", patientId, "taskDocumentsChangeRequests"],
        queryFn: () =>
          api.get("./patients/:patientId/task_change_requests", { path: { patientId } }),
        select: ({ changeRequests }) => changeRequests,
      }),
    medications: (patientId: PatientId) =>
      queryOptions({
        queryKey: ["patient", patientId, "medications"],
        queryFn: () => api.get("./patients/:patientId/medications", { path: { patientId } }),
      }),
    searchMedications: (params: { searchString: string; offset?: number; limit?: number }) =>
      queryOptions({
        queryKey: ["medications", "search", params.searchString, params.offset, params.limit],
        queryFn: () => api.get("./medications/search", { query: params }),
        select: ({ medications }) => medications,
      }),
    taskDocumentRejections: (patientId: PatientId) =>
      queryOptions({
        queryKey: ["patient", patientId, "taskDocumentRejections"],
        queryFn: () =>
          api.get("./patients/:patientId/task_document_rejections", { path: { patientId } }),
        select: (data): components["schemas"]["PatientTaskDocumentRejection"][] => data.rejections,
      }),
    medicalDiagnosisCodes: (patientId: PatientId) =>
      queryOptions({
        queryKey: ["patient", patientId, "medicalDiagnosisCodes"],
        queryFn: () =>
          api.get("./patients/:patientId/patient_medical_diagnosis_codes", {
            path: { patientId },
          }),
        select: ({ patientMedicalDiagnosisCodes }) => patientMedicalDiagnosisCodes,
      }),
    searchIcdCodes: (searchString: string) =>
      queryOptions({
        queryKey: ["icdCodes", "search", searchString],
        queryFn: () =>
          api.anyRequest<{ codes: IcdCodeAutoCompleteResult[] }>(
            "GET",
            "/agencies/:agencyId/agency_members/:agencyMemberId/icd_10_codes/auto_complete",
            { query: { search_string: searchString } }
          ),
        select: ({ codes }) => codes,
        enabled: searchString.length > 0,
      }),
  };
});
